'use client'
import { apiVerifyCaptcha } from '@/apis/captcha';
import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { faPaperPlane, FontAwesomeIcon } from '@/constants/icons';
import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-toastify';

interface Props {
    companyId: string;
    domain: string;
}

const FooterNewsletter = ({ domain, companyId }: Props) => {
    const [email, setEmail] = useState<string>('');
    const [status, setStatus] = useState<'idle' | 'verifying' | 'sending' | 'failed'>('idle');
    const [subscribed, setSubscribed] = useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailStr);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 });
            return;
        }
        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.', { autoClose: 2000 });
            return;
        }
        if (!executeRecaptcha) {
            toast.error('reCAPTCHA not ready. Please try again.', { autoClose: 2000 });
            return;
        }

        try {
            setStatus('verifying');
            const recaptchaToken = await executeRecaptcha('newsletter_form');
            const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);

            if (!captchaRes?.verified) {
                setStatus('failed');
                toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
                return;
            }

            setStatus('sending');
            const response = await apiSubscribeNewsletter(companyId, email);

            if (
                response?.status === 200 ||
                response?.status === true ||
                response?.message === 'Subscribed successfully'
            ) {
                toast.success('Thank you for subscribing!', { autoClose: 2000 });
                setEmail('');
                setSubscribed(true);
            } else {
                toast.error(response?.message || 'You have already subscribed to our newsletter.');
            }
        } catch (err) {
            console.error('Error subscribing:', err);
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2000 });
        } finally {
            setStatus('idle');
        }
    };

    const isSubmitting = status === 'verifying' || status === 'sending';

    return (
        <div className="w-full">
            {/* Heading */}
            <div className="mb-1 flex items-center gap-3">
                <span
                    className="text-xs font-black uppercase tracking-[0.25em]"
                    style={{ color: '#7AFC20' }}
                >
                    Newsletter
                </span>
                {/* neon dot pulse */}
                <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{ background: '#7AFC20' }}
                />
            </div>
            <div className="w-8 h-[2px] rounded-full mb-4" style={{ background: '#7AFC20' }} />

            <p className="text-sm leading-relaxed mb-6" style={{ color: '#F5F5DC', opacity: 0.55 }}>
                Get the latest verified deals and exclusive coupon codes delivered straight to your inbox.
            </p>

            {subscribed ? (
                /* ── Success state ── */
                <div
                    className="flex items-center gap-3 px-5 py-4 rounded-xl"
                    style={{
                        border: '1px solid rgba(122,252,32,0.4)',
                        background: 'rgba(122,252,32,0.07)',
                    }}
                >
                    {/* Checkmark diamond */}
                    <span
                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rotate-45 rounded-sm"
                        style={{ border: '1px solid #7AFC20', background: 'rgba(122,252,32,0.15)' }}
                    >
                        <span className="-rotate-45 text-xs font-black" style={{ color: '#7AFC20' }}>✓</span>
                    </span>
                    <p className="text-sm font-bold" style={{ color: '#7AFC20' }}>
                        You&apos;re subscribed!
                    </p>
                </div>
            ) : (
                /* ── Form ── */
                <form onSubmit={handleSubmit} autoComplete="off" className="w-full">
                    <div
                        className="relative flex items-center w-full rounded-xl overflow-hidden transition-all duration-300 focus-within:shadow-[0_0_0_2px_rgba(122,252,32,0.35)]"
                        style={{
                            border: '1px solid rgba(122,252,32,0.25)',
                            background: 'rgba(8,28,17,0.8)',
                        }}
                    >
                        {/* Left neon bar */}
                        <span
                            className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl"
                            style={{ background: 'linear-gradient(180deg, #7AFC20, rgba(122,252,32,0.2))' }}
                        />

                        <input
                            type="email"
                            placeholder="your@email.com"
                            className="flex-1 bg-transparent border-none outline-none pl-5 pr-3 py-3.5 text-sm focus:ring-0"
                            style={{ color: '#F5F5DC' }}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isSubmitting}
                        />

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            aria-label="Subscribe"
                            className="flex-shrink-0 flex items-center justify-center gap-2 m-1.5 px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_18px_rgba(122,252,32,0.45)] active:scale-95"
                            style={{
                                background: '#7AFC20',
                                color: '#081C11',
                            }}
                        >
                            {isSubmitting ? (
                                <span className="w-4 h-4 border-2 border-[#081C11] border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPaperPlane} className="w-3 h-3" />
                                    <span className="hidden sm:inline">Subscribe</span>
                                </>
                            )}
                        </button>
                    </div>

                    <p className="mt-3 text-[11px]" style={{ color: '#F5F5DC', opacity: 0.3 }}>
                        No spam. Unsubscribe anytime.
                    </p>
                </form>
            )}
        </div>
    );
};

export default FooterNewsletter;
